import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Menu,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'
import LocaleToggle from '../components/LocaleToggle'
import { useLanguageStore } from '../stores/languageStore'

export default function Navigations() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingNavigation, setEditingNavigation] = useState(null)
  const [showOtherLocales, setShowOtherLocales] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'custom',
    is_active: true,
  })

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentLanguageCode, currentLanguageId } = useLanguageStore()

  const {
    data: navigations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['navigations', currentLanguageCode, showOtherLocales],
    queryFn: () =>
      api
        .get('/navigations', {
          params: {
            locale: currentLanguageCode || 'en',
            include_other_locales: showOtherLocales,
          },
        })
        .then((res) => res.data),
    placeholderData: (previousData) => previousData,
  })

  const filteredNavigations = navigations.filter(
    (nav) =>
      nav.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nav.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nav.type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const createNavigationMutation = useMutation({
    mutationFn: (data) => api.post('/navigations', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['navigations'])
      setShowAddForm(false)
      resetForm()
    },
  })

  const updateNavigationMutation = useMutation({
    mutationFn: ({ slug, data }) => api.put(`/navigations/${slug}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['navigations'])
      setEditingNavigation(null)
      resetForm()
    },
  })

  const deleteNavigationMutation = useMutation({
    mutationFn: (slug) => api.delete(`/navigations/${slug}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['navigations'])
    },
  })

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      type: 'custom',
      is_active: true,
    })
  }

  const handleEdit = (navigation) => {
    setEditingNavigation(navigation)
    setFormData({
      name: navigation.name,
      slug: navigation.slug,
      type: navigation.type,
      is_active: navigation.is_active,
    })
    setShowAddForm(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (editingNavigation) {
      updateNavigationMutation.mutate({
        slug: editingNavigation.slug,
        data: formData,
      })
    } else {
      const dataWithLanguage = {
        ...formData,
        language_id: currentLanguageId,
      }
      createNavigationMutation.mutate(dataWithLanguage)
    }
  }

  const handleDelete = (navigation) => {
    if (
      window.confirm(
        `Are you sure you want to delete navigation "${navigation.name}"?`
      )
    ) {
      deleteNavigationMutation.mutate(navigation.slug)
    }
  }

  const handleManageItems = (navigation) => {
    navigate(`/navigations/${navigation.slug}/builder`)
  }

  const getTypeLabel = (type) => {
    const types = {
      header: 'Header',
      footer: 'Footer',
      custom: 'Custom',
    }
    return types[type] || type
  }

  const getTypeColor = (type) => {
    const colors = {
      header: 'bg-blue-100 text-blue-800',
      footer: 'bg-purple-100 text-purple-800',
      custom: 'bg-gray-100 text-gray-800',
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Navigations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage navigation menus for your site.
          </p>
        </div>
        <div className="flex gap-3">
          <LocaleToggle
            enabled={showOtherLocales}
            onChange={setShowOtherLocales}
          />
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Navigation
          </Button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search navigations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingNavigation ? 'Edit Navigation' : 'Add New Navigation'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Navigation Name
                </label>
                <Input
                  placeholder="e.g., Main Menu"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Slug
                </label>
                <Input
                  placeholder="e.g., main-menu"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="header">Header</option>
                  <option value="footer">Footer</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active
                  </span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAddForm(false)
                  setEditingNavigation(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createNavigationMutation.isPending ||
                  updateNavigationMutation.isPending
                }
              >
                {editingNavigation ? 'Update Navigation' : 'Add Navigation'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Available Navigations
          </h3>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading navigations...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500">
                Error loading navigations: {error.message}
              </div>
            </div>
          ) : filteredNavigations.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">
                {searchTerm
                  ? 'No navigations found matching your search.'
                  : 'No navigations configured yet.'}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredNavigations.map((navigation) => {
                    const isMissingInCurrentLocale =
                      !navigation.has_translation_for_locale
                    return (
                      <tr
                        key={navigation.id}
                        className={
                          isMissingInCurrentLocale
                            ? 'opacity-50 bg-gray-50'
                            : ''
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Menu className="h-4 w-4 text-gray-400" />
                            <div
                              className={`text-sm font-medium ${
                                isMissingInCurrentLocale
                                  ? 'text-gray-500'
                                  : 'text-gray-900'
                              }`}
                            >
                              {navigation.name}
                            </div>
                            {isMissingInCurrentLocale && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                                Missing Translation
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`text-sm font-mono ${
                              isMissingInCurrentLocale
                                ? 'text-gray-400'
                                : 'text-gray-900'
                            }`}
                          >
                            {navigation.slug}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                              navigation.type
                            )}`}
                          >
                            {getTypeLabel(navigation.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {navigation.all_items?.length || 0} items
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              navigation.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {navigation.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleManageItems(navigation)}
                              className="text-primary-400 hover:text-primary-600"
                              title="Manage Items"
                            >
                              <Menu className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(navigation)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(navigation)}
                              className="text-red-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
